import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { Lang } from "@/components/luminous/primitives";

/**
 * Diagramas explicativos por familia de servicio.
 * Cada variante explica un mecanismo distinto: no es la misma gráfica
 * con otro título. Se trazan una sola vez al entrar en viewport y después
 * quedan quietos; sin JavaScript se ven completos.
 */

export type DiagramVariant =
  | "architecture"
  | "intent"
  | "markets"
  | "identity"
  | "migration"
  | "catalog"
  | "answers"
  | "priority"
  | "local";

/** Familia visual por página de servicio (ids reales de money-pages). */
export const diagramForService = (id: string): DiagramVariant => {
  switch (id) {
    case "technical-seo":
    case "seo-audit":
      return "architecture";
    case "google-ads":
    case "seo-sem-strategies":
      return "intent";
    case "international-seo":
    case "hispanic-seo":
      return "markets";
    case "branding":
      return "identity";
    case "website-redesign":
    case "rebranding-seo":
      return "migration";
    case "amazon-seo":
    case "ecommerce-seo":
      return "catalog";
    case "ai-search":
      return "answers";
    default:
      return "priority";
  }
};

const INK = "var(--v3-ink)";
const SOFT = "var(--v3-ink-soft)";
const BLUE = "var(--v3-blue)";
const COPPER = "var(--v3-copper)";
const LINE = "rgba(13,31,60,0.18)";

type Copy = { title: string; caption: string; nodes: string[]; notes?: string[] };

const copy: Record<DiagramVariant, Record<Lang, Copy>> = {
  architecture: {
    es: {
      title: "De la URL al resultado: dónde se rompe el acceso",
      caption:
        "Recorrido técnico real: si una etapa bloquea, las siguientes no ocurren. El trabajo consiste en localizar la etapa que detiene el acceso, no en publicar más páginas.",
      nodes: ["Rastreo", "Renderizado", "Indexación", "Resultado"],
      notes: [
        "robots, enlaces internos, presupuesto de rastreo",
        "JavaScript, contenido que no llega al HTML",
        "canonical, duplicados, paginación",
        "consulta, intención y página correcta",
      ],
    },
    en: {
      title: "From URL to result: where access breaks",
      caption:
        "The real technical path: if one stage blocks, the next ones never happen. The work is finding the stage that stops access, not publishing more pages.",
      nodes: ["Crawl", "Render", "Index", "Result"],
      notes: [
        "robots, internal links, crawl budget",
        "JavaScript, content missing from the HTML",
        "canonical, duplicates, pagination",
        "query, intent and the right page",
      ],
    },
  },
  intent: {
    es: {
      title: "Intención, página de destino y contacto medido",
      caption:
        "Cada búsqueda pagada solo tiene sentido si la página de destino responde a esa intención y el contacto queda registrado. Sin medición no hay decisión de inversión.",
      nodes: ["Intención de búsqueda", "Anuncio", "Página de destino", "Contacto medido"],
      notes: [
        "problema, urgencia y presupuesto del comprador",
        "mensaje y términos negativos",
        "oferta concreta, prueba y formulario",
        "llamada, formulario o cotización atribuida",
      ],
    },
    en: {
      title: "Intent, landing page and measured contact",
      caption:
        "A paid search only makes sense if the landing page answers that intent and the contact is recorded. Without measurement there is no investment decision.",
      nodes: ["Search intent", "Ad", "Landing page", "Measured contact"],
      notes: [
        "buyer problem, urgency and budget",
        "message and negative terms",
        "concrete offer, proof and form",
        "call, form or quote attributed",
      ],
    },
  },
  markets: {
    es: {
      title: "Un mercado por idioma, no una traducción",
      caption:
        "Cada mercado tiene su propia demanda, competencia y vocabulario. La arquitectura separa los mercados y hreflang indica al buscador cuál corresponde a cada usuario.",
      nodes: ["Demanda por mercado", "Arquitectura por idioma", "hreflang", "Contenido local"],
      notes: [
        "términos y competencia distintos en cada país",
        "rutas estables por idioma",
        "correspondencia recíproca entre versiones",
        "ejemplos, moneda y contacto del mercado",
      ],
    },
    en: {
      title: "One market per language, not a translation",
      caption:
        "Each market has its own demand, competition and vocabulary. The architecture separates markets and hreflang tells the engine which version belongs to each user.",
      nodes: ["Demand per market", "Architecture per language", "hreflang", "Local content"],
      notes: [
        "different terms and competitors per country",
        "stable routes per language",
        "reciprocal mapping between versions",
        "market examples, currency and contact",
      ],
    },
  },
  identity: {
    es: {
      title: "Decisiones de identidad y sus aplicaciones",
      caption:
        "La identidad se decide una vez y se aplica en piezas concretas y autorizadas: empaque, punto de venta y presencia digital. Cada aplicación se deriva de la misma decisión.",
      nodes: ["Decisión de marca", "Sistema", "Aplicaciones"],
      notes: [
        "posicionamiento, nombre y personalidad",
        "color, tipografía, símbolo y jerarquía",
        "empaque, anaquel, web y materiales",
      ],
    },
    en: {
      title: "Identity decisions and their applications",
      caption:
        "Identity is decided once and applied to concrete, authorized pieces: packaging, point of sale and digital presence. Every application derives from the same decision.",
      nodes: ["Brand decision", "System", "Applications"],
      notes: [
        "positioning, name and personality",
        "colour, typography, symbol and hierarchy",
        "packaging, shelf, website and materials",
      ],
    },
  },
  migration: {
    es: {
      title: "Rediseñar sin perder lo que ya funciona",
      caption:
        "Un rediseño cambia experiencia y arquitectura, pero conserva URLs, contenido con demanda y enlaces. Lo que cambia de dirección se redirige y se verifica después de publicar.",
      nodes: ["Sitio actual", "Inventario", "Nueva arquitectura", "Publicación verificada"],
      notes: [
        "páginas con tráfico, enlaces y conversión",
        "qué se conserva, qué se une, qué se retira",
        "navegación, plantillas y conversión",
        "redirecciones, rastreo y seguimiento",
      ],
    },
    en: {
      title: "Redesign without losing what already works",
      caption:
        "A redesign changes experience and architecture but keeps URLs, content with demand and links. Anything that moves is redirected and verified after launch.",
      nodes: ["Current site", "Inventory", "New architecture", "Verified launch"],
      notes: [
        "pages with traffic, links and conversion",
        "what stays, what merges, what is retired",
        "navigation, templates and conversion",
        "redirects, crawling and tracking",
      ],
    },
  },
  catalog: {
    es: {
      title: "Catálogo: cómo encuentra el comprador un producto",
      caption:
        "El comprador no busca el nombre del producto: busca uso, medida o compatibilidad. El catálogo debe estar organizado por esa forma de buscar, no por el orden interno del ERP.",
      nodes: ["Uso y medida", "Categoría", "Ficha de producto", "Compra"],
      notes: [
        "cómo nombra el comprador lo que necesita",
        "agrupación sin duplicados ni canibalización",
        "título, imágenes y atributos legibles",
        "disponibilidad, precio y reseñas",
      ],
    },
    en: {
      title: "Catalogue: how a buyer finds a product",
      caption:
        "Buyers don't search the product name: they search use, size or compatibility. The catalogue must follow how they search, not the internal ERP order.",
      nodes: ["Use and size", "Category", "Product page", "Purchase"],
      notes: [
        "how the buyer names what they need",
        "grouping without duplicates or cannibalisation",
        "title, images and legible attributes",
        "availability, price and reviews",
      ],
    },
  },
  answers: {
    es: {
      title: "Cuando la respuesta la redacta un modelo",
      caption:
        "Los asistentes citan fuentes que pueden leer, entender y atribuir. El trabajo es que la información de la empresa sea accesible, coherente y verificable.",
      nodes: ["Pregunta", "Fuentes leídas", "Respuesta citada", "Visita"],
      notes: [
        "la consulta se formula en lenguaje natural",
        "HTML accesible, datos estructurados, coherencia",
        "cita con atribución a la marca",
        "tráfico cualificado y contacto",
      ],
    },
    en: {
      title: "When the answer is written by a model",
      caption:
        "Assistants cite sources they can read, understand and attribute. The work is making the company's information accessible, consistent and verifiable.",
      nodes: ["Question", "Sources read", "Cited answer", "Visit"],
      notes: [
        "the query is phrased in natural language",
        "accessible HTML, structured data, consistency",
        "citation attributed to the brand",
        "qualified traffic and contact",
      ],
    },
  },
  priority: {
    es: {
      title: "Qué se decide primero",
      caption:
        "El orden importa más que la lista de tareas: primero se entiende la demanda real, después se corrige lo que impide competir y solo entonces se amplía.",
      nodes: ["Demanda", "Diagnóstico", "Prioridad", "Ejecución"],
      notes: [
        "qué busca el mercado alcanzable",
        "qué impide competir hoy",
        "impacto frente a esfuerzo",
        "entregables y responsables",
      ],
    },
    en: {
      title: "What gets decided first",
      caption:
        "Order matters more than the task list: first understand real demand, then fix what blocks competing, and only then expand.",
      nodes: ["Demand", "Diagnosis", "Priority", "Execution"],
      notes: [
        "what the reachable market searches",
        "what blocks competing today",
        "impact versus effort",
        "deliverables and owners",
      ],
    },
  },
  local: {
    es: {
      title: "Área de servicio y visibilidad local",
      caption:
        "La visibilidad local depende de dónde se presta el servicio y de la coherencia de los datos de la empresa, no de crear oficinas que no existen.",
      nodes: ["Sede real", "Área de servicio", "Ficha y datos", "Contacto local"],
      notes: [
        "una dirección verificable",
        "municipios donde sí se trabaja",
        "nombre, dirección y teléfono coherentes",
        "llamada, ruta o formulario",
      ],
    },
    en: {
      title: "Service area and local visibility",
      caption:
        "Local visibility depends on where the service is delivered and on consistent business data, not on inventing offices that do not exist.",
      nodes: ["Real office", "Service area", "Listing and data", "Local contact"],
      notes: [
        "one verifiable address",
        "areas actually served",
        "consistent name, address and phone",
        "call, directions or form",
      ],
    },
  },
};

/**
 * Mecanismo de lectura por variante. No es la misma gráfica con otro título:
 * ARQUITECTURA (dependencias encadenadas), RECORRIDO (estaciones abiertas) y
 * SISTEMA (núcleo y aplicaciones) se explican de forma distinta.
 */
type Mechanism = "architecture" | "journey" | "system";

const MECHANISM: Record<DiagramVariant, Mechanism> = {
  architecture: "architecture",
  priority: "architecture",
  intent: "journey",
  migration: "journey",
  catalog: "journey",
  answers: "journey",
  local: "journey",
  identity: "system",
  markets: "system",
};

const LABELS: Record<Mechanism, Record<Lang, { kind: string; relation: (next: string) => string }>> = {
  architecture: {
    es: { kind: "Arquitectura", relation: (n) => `Si esta etapa falla, no ocurre: ${n}` },
    en: { kind: "Architecture", relation: (n) => `If this stage fails, this never happens: ${n}` },
  },
  journey: {
    es: { kind: "Recorrido", relation: (n) => `Siguiente estación: ${n}` },
    en: { kind: "Journey", relation: (n) => `Next station: ${n}` },
  },
  system: {
    es: { kind: "Sistema", relation: (n) => `Se deriva de: ${n}` },
    en: { kind: "System", relation: (n) => `Derived from: ${n}` },
  },
};

const UI: Record<Lang, { stage: string; detail: string; last: string; core: string }> = {
  es: {
    stage: "Etapas",
    detail: "Etapa seleccionada",
    last: "Cierre del recorrido",
    core: "Núcleo",
  },
  en: {
    stage: "Stages",
    detail: "Selected stage",
    last: "End of the path",
    core: "Core",
  },
};

/** Traza el acento una sola vez al entrar en viewport y después queda quieto. */
const useDrawOnce = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(false);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setDrawn(true);
      setSettled(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setDrawn(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!drawn || settled) return;
    const id = window.setTimeout(() => setSettled(true), 1200);
    return () => window.clearTimeout(id);
  }, [drawn, settled]);

  return { ref, drawn, settled };
};

/** Conexión horizontal (desktop): línea base + acento que alcanza la etapa activa. */
const Connector = ({
  count,
  active,
  drawn,
  settled,
}: {
  count: number;
  active: number;
  drawn: boolean;
  settled: boolean;
}) => {
  const fraction = count > 1 ? (active + 0.5) / count : 1;
  return (
    <svg
      viewBox="0 0 1000 10"
      preserveAspectRatio="none"
      className="hidden h-[10px] w-full md:block"
      aria-hidden="true"
      focusable="false"
    >
      <line x1="0" y1="5" x2="1000" y2="5" stroke={LINE} strokeWidth="1.5" />
      <line
        x1="0"
        y1="5"
        x2="1000"
        y2="5"
        stroke={BLUE}
        strokeWidth="2.5"
        pathLength={1}
        strokeDasharray={1}
        strokeDashoffset={drawn ? 1 - fraction : 1}
        style={{
          transition: `stroke-dashoffset ${settled ? "280ms" : "1200ms"} var(--v3-ease)`,
        }}
      />
      {Array.from({ length: count }).map((_, i) => {
        const cx = ((i + 0.5) / count) * 1000;
        const on = i <= active;
        return (
          <circle
            key={i}
            cx={cx}
            cy="5"
            r={i === active ? 4.5 : 3}
            fill={i === count - 1 && on ? COPPER : on ? BLUE : "var(--v3-mineral-deep)"}
            stroke={on ? "none" : LINE}
            strokeWidth="1.5"
          />
        );
      })}
    </svg>
  );
};

export const ServiceDiagram = ({
  variant,
  language,
  className = "",
}: {
  variant: DiagramVariant;
  language: Lang;
  className?: string;
}) => {
  const { ref, drawn, settled } = useDrawOnce();
  const c = copy[variant][language];
  const mech = MECHANISM[variant];
  const labels = LABELS[mech][language];
  const ui = UI[language];
  const [active, setActive] = useState(0);
  const n = c.nodes.length;
  const isSystem = mech === "system";
  const first = c.nodes[0];

  // En "sistema" el primer nodo es el núcleo y el resto son aplicaciones.
  const stages = isSystem ? c.nodes.slice(1) : c.nodes;
  const stageNotes = isSystem ? (c.notes ?? []).slice(1) : (c.notes ?? []);
  const count = stages.length;
  const safeActive = Math.min(active, count - 1);
  const next = mech === "system" ? first : c.nodes[safeActive + (isSystem ? 2 : 1)];

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "ArrowDown" && e.key !== "ArrowUp")
      return;
    e.preventDefault();
    const dir = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : -1;
    setActive((i) => (i + dir + count) % count);
  };

  return (
    <figure className={`min-w-0 max-w-full ${className ?? ""}`} ref={ref}>
      <p className="v3-micro">
        {labels.kind}
        <span className="mx-2" aria-hidden>
          ·
        </span>
        {ui.stage}
      </p>
      <p className="mt-3 text-[1.05rem] font-semibold text-[color:var(--v3-ink)]">{c.title}</p>

      <div className="mt-6 min-w-0 border border-[color:var(--v3-line)] bg-[color:var(--v3-mineral-deep)] p-5 lg:p-7">
        {isSystem ? (
          <div className="mb-5 border-l-2 border-[color:var(--v3-copper)] bg-[color:var(--v3-white)] p-4">
            <p className="v3-micro" style={{ color: COPPER }}>
              {ui.core}
            </p>
            <p className="mt-2 text-[1rem] font-semibold text-[color:var(--v3-ink)]">{first}</p>
            {c.notes?.[0] ? <p className="v3-note mt-1">{c.notes[0]}</p> : null}
          </div>
        ) : null}

        <Connector count={count} active={safeActive} drawn={drawn} settled={settled} />

        <ol
          className={`mt-4 grid gap-px bg-[color:var(--v3-line)] ${
            count >= 4 ? "md:grid-cols-4" : count === 3 ? "md:grid-cols-3" : "md:grid-cols-2"
          }`}
          onKeyDown={onKeyDown}
        >
          {stages.map((node, i) => {
            const on = i === safeActive;
            return (
              <li key={node} className="min-w-0">
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => setActive(i)}
                  className="flex min-h-[3rem] w-full flex-col items-start gap-1 border-t-[3px] bg-[color:var(--v3-white)] p-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--v3-blue)]"
                  style={{
                    borderTopColor: on
                      ? i === count - 1
                        ? COPPER
                        : BLUE
                      : "transparent",
                    background: on ? "var(--v3-white)" : "rgba(255,255,255,0.55)",
                    transition: "background var(--v3-t-control) var(--v3-ease), border-color var(--v3-t-control) var(--v3-ease)",
                  }}
                >
                  <span className="v3-micro" style={{ color: on ? BLUE : SOFT }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className="text-[0.98rem] font-semibold leading-snug"
                    style={{ color: INK }}
                  >
                    {node}
                  </span>
                  {stageNotes[i] ? (
                    <span className="text-[0.82rem] leading-snug" style={{ color: SOFT }}>
                      {stageNotes[i]}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ol>

        <div
          key={safeActive}
          className="v3-swap mt-5 border-l-2 border-[color:var(--v3-blue)] bg-[color:var(--v3-white)] p-5"
        >
          <p className="v3-micro">{ui.detail}</p>
          <p className="mt-2 text-[1.05rem] font-semibold text-[color:var(--v3-ink)]">
            {stages[safeActive]}
          </p>
          {stageNotes[safeActive] ? (
            <p className="v3-body mt-2 text-[0.95rem]">{stageNotes[safeActive]}</p>
          ) : null}
          <p className="mt-3 text-[0.85rem]" style={{ color: next ? BLUE : COPPER }}>
            {next ? labels.relation(next) : ui.last}
          </p>
        </div>
      </div>

      <figcaption className="v3-caption mt-4">{c.caption}</figcaption>
    </figure>
  );
};
